import {
  ActiveDashboardLayoutItem,
  ActiveDashboardSectionLayout,
  RawWidgetsLayout,
  WidgetDefinition,
  WidgetsLayout,
} from '@types';
import { BrowserClient } from '@sentry/browser';
import { widgetMissingFromDefinitions, SentryMetaData } from '@services/sentry';

interface PermittedParams {
  userPermissions: string[];
  widget: WidgetDefinition;
}

export const mapLayout = ({
  sentry,
  sentryMetaData,
  layout,
  userPermissions,
}: {
  sentry: BrowserClient;
  sentryMetaData: SentryMetaData;
  layout: RawWidgetsLayout;
  userPermissions: string[];
}): WidgetsLayout => {
  let activeDashboardSection;

  if (layout.activeDashboardSection) {
    activeDashboardSection = getActiveDashboardSection(
      sentry,
      sentryMetaData,
      layout.activeDashboardSection,
      userPermissions,
    );
  }

  const mappedLayout: WidgetsLayout = {
    ...getMainLayout(layout, userPermissions),
    ...(activeDashboardSection ? { activeDashboardSection } : undefined),
  };

  return mappedLayout;
};

const isWidgetPermitted = ({ userPermissions, widget }: PermittedParams): Boolean =>
  !widget.permissions ||
  widget.permissions?.every((permission: string) => userPermissions.includes(permission));

const getMainLayout = (layout: RawWidgetsLayout, userPermissions: string[]) => {
  const filterWidgets = (widgets: WidgetDefinition[]) => {
    return widgets.filter(widget =>
      isWidgetPermitted({
        userPermissions,
        widget,
      }),
    );
  };

  return {
    column1: filterWidgets(layout.column1),
    column2: filterWidgets(layout.column2),
  };
};

const getActiveDashboardSection = (
  sentry: BrowserClient,
  sentryMetaData: SentryMetaData,
  activeDashboardSection: ActiveDashboardSectionLayout,
  userPermissions: string[],
) => {
  const filterWidgets = (widgets: ActiveDashboardLayoutItem[]) => {
    const definitions = activeDashboardSection?.definitions || {};

    return widgets.filter(widget => {
      const widgetDefinition = definitions[widget.widgetId];

      if (!widgetDefinition) {
        widgetMissingFromDefinitions(sentry, sentryMetaData, widget.widgetId);
      }

      return (
        widgetDefinition &&
        isWidgetPermitted({
          userPermissions,
          widget: widgetDefinition,
        })
      );
    });
  };

  return {
    left: filterWidgets(activeDashboardSection?.left),
    right: filterWidgets(activeDashboardSection?.right),
    definitions: activeDashboardSection.definitions,
    removed: activeDashboardSection.removed,
    available: activeDashboardSection.available,
  };
};
